import { useEffect, useRef, useState } from "react";

export type CanvasSelectionMode = "add" | "subtract";

// Same reasoning as RectangleSelection.tsx's own DRAG_THRESHOLD_PX: below
// this many screen pixels of movement between mousedown and mouseup, treat
// it as a click, not a drag — a Shift/Ctrl+click on a chip is handled
// entirely by the chip's own onClick (SubBasinCanvas.tsx), which fires
// independently of this component. Without this threshold, a Shift/Ctrl
// click (no real drag) would ALSO compute a degenerate zero-size rectangle
// here, double-processing the same toggle.
const DRAG_THRESHOLD_PX = 4;

interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
  mode: CanvasSelectionMode;
}

/**
 * DOM equivalent of RectangleSelection.tsx's Leaflet-based rectangle select,
 * for SubBasinCanvas.tsx's plain HTML chip grid: Shift+drag ADDS whatever
 * chip the rectangle overlaps to the selection, Ctrl+drag SUBTRACTS it —
 * never toggles, same "never toggles" reasoning as the map version (a
 * rectangle that merely grazes a chip shouldn't flip it unpredictably).
 *
 * Viewport coordinates throughout (`clientX`/`clientY`,
 * `getBoundingClientRect()`) rather than coordinates relative to some
 * container: SubBasinCanvas.tsx's own root scrolls internally
 * (`overflow-y: auto`), so anchoring to it would require compensating for
 * scroll offset for no benefit — the overlay renders `position: fixed`, and
 * intersecting chips are found by comparing their own
 * `getBoundingClientRect()` (already viewport-relative) against the drag
 * rectangle directly (see `onSelect` in SubBasinCanvas.tsx).
 *
 * Mousedown is caught on `document`, not scoped to the chip grid itself —
 * deliberately: "Edit project"'s sidebar sits on TOP of the chip grid as an
 * overlapping sibling (`position: absolute`, same trap as `.edit-panel`
 * over the map, see CLAUDE.md), so a drag that starts or passes over it
 * needs the exact same handling, or the browser's native text-selection
 * takes over instead (a real bug hit while testing this — Shift-dragging
 * from over the sidebar highlighted its labels in blue rather than
 * rectangle-selecting). Excluded: form controls (`input`/`textarea`/
 * `select`/`[contenteditable]`) — those need to keep their own native
 * text-selection (e.g. selecting text inside "Zone name" to copy it),
 * which a page-wide handler would otherwise break. A drag that starts
 * somewhere with nothing selectable underneath (e.g. the top bar) still
 * renders the rectangle and calls `onSelect`, which is a no-op if it
 * touches no `[data-subbasin-code]` chip — harmless, not worth excluding
 * separately.
 *
 * The active drag (start point, mode) lives in a plain closure variable,
 * not React state — only the rectangle's on-screen box is state (needed to
 * render the overlay). This matters for two reasons: calling `onSelect`
 * (which triggers a DIFFERENT component's setState, in SubBasinCanvas.tsx)
 * from inside a `setState` updater function throws a React warning
 * ("Cannot update a component while rendering a different component") —
 * plain native event handlers don't have that restriction, so `onSelect`
 * is called directly from `onMouseUp`, never nested inside `setBox`.
 * Second, the listeners are attached once, on mount (empty deps) rather
 * than re-subscribed on every mousemove — re-subscribing while a drag is
 * in progress (which a naive `[drag]` dependency would do) would reset the
 * closure's start point mid-drag. `onSelect` itself is read through a ref
 * kept fresh every render (the usual "latest callback" pattern) so a
 * parent re-render during an active drag (e.g. from an unrelated state
 * change) doesn't tear down and lose that drag either.
 */
export function CanvasRectangleSelect({
  onSelect,
}: {
  onSelect: (rect: DOMRect, mode: CanvasSelectionMode) => void;
}) {
  const [box, setBox] = useState<Box | null>(null);
  const onSelectRef = useRef(onSelect);
  useEffect(() => {
    onSelectRef.current = onSelect;
  });

  useEffect(() => {
    let drag: { startX: number; startY: number; mode: CanvasSelectionMode } | null = null;

    function onMouseDown(e: MouseEvent) {
      const mode: CanvasSelectionMode | null = e.shiftKey
        ? "add"
        : e.ctrlKey || e.metaKey
          ? "subtract"
          : null;
      if (!mode) return;
      const target = e.target as HTMLElement | null;
      if (target?.closest("input, textarea, select, [contenteditable]")) return;
      // Without this, dragging with Shift/Ctrl held triggers the browser's
      // native text-selection highlight over every label/cell the
      // rectangle passes — same reasoning as RectangleSelection.tsx's own
      // `map.dragging.disable()` while its rectangle is active.
      e.preventDefault();
      drag = { startX: e.clientX, startY: e.clientY, mode };
      setBox({ left: e.clientX, top: e.clientY, width: 0, height: 0, mode });
    }

    function onMouseMove(e: MouseEvent) {
      if (!drag) return;
      setBox({
        left: Math.min(drag.startX, e.clientX),
        top: Math.min(drag.startY, e.clientY),
        width: Math.abs(e.clientX - drag.startX),
        height: Math.abs(e.clientY - drag.startY),
        mode: drag.mode,
      });
    }

    function onMouseUp(e: MouseEvent) {
      if (!drag) return;
      const { startX, startY, mode } = drag;
      drag = null;
      setBox(null);
      const moved = Math.hypot(e.clientX - startX, e.clientY - startY);
      if (moved > DRAG_THRESHOLD_PX) {
        const left = Math.min(startX, e.clientX);
        const top = Math.min(startY, e.clientY);
        const right = Math.max(startX, e.clientX);
        const bottom = Math.max(startY, e.clientY);
        onSelectRef.current(new DOMRect(left, top, right - left, bottom - top), mode);
      }
    }

    document.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  if (!box) return null;
  return (
    <div
      className={`canvas-drag-box canvas-drag-box-${box.mode}`}
      style={{ left: box.left, top: box.top, width: box.width, height: box.height }}
    />
  );
}
